import React, { useEffect, useState } from "react";
import Link from "@mui/material/Link";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { authenticate } from "store/slices/auth";
import { APILogout, APIValidateToken, AVATARGetUserAvatarUrl } from "utils/api";
import "./Header.scss";
import { AUTH_STATUS } from "components/Modals/AuthModal";
import { triggerOpenModal } from "store/slices/modals";
import { Avatar } from "@mui/material";
import { removeCognitoTokensFromLocalStorage } from "utils/cognito";
import { initialsFromName } from "utils/utils";
import ProfilePopup from "components/ProfilePopup";
import NotificationsPopup from "components/NotificationsPopup";
import MorePopup from "components/MorePopup";
import { useTranslation } from "react-i18next";

/**
 * Strict TypeScript types for header config
 */
export type HeaderMenuItem = {
  label: string;
  href?: string;
  newTab?: boolean;
  action?: () => void;
  icon?: React.ReactNode;
  showWhenLoggedIn?: boolean;
  showWhenLoggedOut?: boolean;
  hideMeFilter?: boolean;
  external?: boolean;
  highlight?: boolean;
  children?: HeaderMenuItem[];
};

export type HeaderLogoConfig = {
  src: string;
  alt: string;
  href?: string;
  action?: () => void;
  external?: boolean;
};

export type HeaderMenuConfig = {
  journey: "learner" | "recruiter" | "mentor";
  logo: HeaderLogoConfig;
  navigation: HeaderMenuItem[];
  navigationPopover?: HeaderMenuItem[];
  actions?: HeaderMenuItem[];
  signUpAction?: () => void;
  loginAction?: () => void;
};

export const LEARNER_HOME = process.env.REACT_APP_LANDING_URL;
export const RECRUITER_HOME = `${process.env.REACT_APP_LANDING_URL}/recruiter`;
export const RECRUITER_HOME_TEST_CATALOG = `${RECRUITER_HOME}#featured-labs`;
export const RECRUITER_HOME_JOIN_FORM= `${RECRUITER_HOME}#join-us-as-recruiter`;
export const MENTOR_HOME = `${process.env.REACT_APP_LANDING_URL}/join-us-as-mentor`;
export const MENTOR_HOME_JOIN_FORM = `${MENTOR_HOME}#mentor-onboarding-form-section`;

const Header: React.FC = () => {
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const user = useAppSelector((state) => state.auth.user_me);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const learnerMenu: HeaderMenuConfig = {
    journey: "learner",
    logo: {
      src: require("assets/logo.svg").default,
      alt: "Scalyz Logo",
      href: `${process.env.REACT_APP_LANDING_URL}`,
    },
    navigation: [
      { label: "Home", href: "/" },
      { label: "Labs", href: "/labs" },
      { label: "Community", href: `${process.env.REACT_APP_LANDING_URL}/community` },
      {
        label: "More",
        children: [
          {
            label: "Join as Mentor",
            href: MENTOR_HOME,
            icon: <img className="max-w-[24px] max-h-[24px]" src={require("assets/mentor-icon.svg").default} alt="mentor" />,
          },
          {
            label: "Join as Recruiter",
            href: RECRUITER_HOME,
            icon: <img className="max-w-[24px] max-h-[24px]" src={require("assets/recruiter-icon.svg").default} alt="recruiter" />,
          },
          {
            label: "Pricing",
            href: `${process.env.REACT_APP_LANDING_URL}/pricing`,
            icon: <img className="max-w-[18px] max-h-[18px]" src={require("assets/pricing-icon.svg").default} alt="pricing" />,
          },
          {
            label: "Contact",
            action: () => {
              let w = window as any;
              if (w.HubSpotConversations && w.HubSpotConversations.widget) {
                w.HubSpotConversations.widget.open();
              }
            },
            icon: <img className="max-w-[17px] max-h-[17px]" src={require("assets/contact-icon.svg").default} alt="contact" />,
          },
        ],
      },
    ],
    actions: [
      {
        label: "Profile",
        href: "/profile/personal-details",
        showWhenLoggedIn: true,
        icon: <img className="max-w-[16px] max-h-[16px]" src={require("assets/user.svg").default} alt="profile" />,
      },
      {
        label: "Manage your plan",
        href: "/profile/manage-plan",
        showWhenLoggedIn: true,
        icon: <img className="max-w-[16px] max-h-[16px]" src={require("assets/manage-plan-icon.svg").default} alt="manage plan" />,
      },
      {
        label: "Help",
        action: () => {
          let w = window as any;
          if (w.HubSpotConversations && w.HubSpotConversations.widget) {
            w.HubSpotConversations.widget.open();
          }
        },
        showWhenLoggedIn: true,
        icon: <img className="max-w-[16px] max-h-[16px]" src={require("assets/help_icon.svg").default} alt="help" />,
      },
      {
        label: "Logout",
        action: undefined,
        showWhenLoggedIn: true,
        highlight: true,
        icon: <img className="max-w-[16px] max-h-[16px]" src={require("assets/log-out-red.svg").default} alt="logout" />,
      },
    ],
    signUpAction: () => {
      dispatch(triggerOpenModal("modal-auth", { status: AUTH_STATUS.SIGNUP }));
    },
    loginAction: () => {
      dispatch(triggerOpenModal("modal-auth", { status: AUTH_STATUS.LOGIN }));
    },
  };

  // Auth modal
  const handleOpenSignUpModal = () =>
    dispatch(triggerOpenModal("modal-auth", { status: AUTH_STATUS.SIGNUP }));
  const handleOpenLoginModal = () =>
    dispatch(triggerOpenModal("modal-auth", { status: AUTH_STATUS.LOGIN }));

  // Get user info on app start
  useEffect(() => {
    APIValidateToken()
      .then((res) => res.data)
      .then((user_data) => {
        dispatch(authenticate(true, user_data));
      })
      .catch((error) => {
        dispatch(authenticate(false, null));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Auth modal logic for landing page
  useEffect(() => {
    if (pathname === "/" && searchParams.has("login") && !isLoggedIn) {
      handleOpenLoginModal();
    }
    if (pathname === "/" && searchParams.has("login") && isLoggedIn && searchParams.has("forward")) {
      const forward = searchParams.get("forward");
      if (forward && forward.startsWith(process.env.REACT_APP_LANDING_URL || "")) {
        window.location.href = forward;
      } else if (forward) {
        navigate(forward);
      }
    }
    if (pathname === "/" && searchParams.has("signup") && !isLoggedIn) {
      handleOpenSignUpModal();
    }
    if (pathname === "/" && searchParams.has("signup") && !isLoggedIn && !searchParams.has("forward")) {
      const forward = searchParams.get("forward");
      if (forward && forward.startsWith(process.env.REACT_APP_LANDING_URL || "")) {
        window.location.href = forward;
      } else if (forward) {
        navigate(forward);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, isLoggedIn]);

  // Popover state
  const [anchorElMorePopup, setAnchorElMorePopup] = useState<null | HTMLElement>(null);
  const handleClickMorePopup = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMorePopup(event.currentTarget);
  };
  const handleCloseMorePopup = () => {
    setAnchorElMorePopup(null);
  };
  const openMorePopup = Boolean(anchorElMorePopup);
  const idMorePopup = openMorePopup ? "more-popup" : undefined;

  const [anchorElProfilePopup, setAnchorElProfilePopup] = useState<null | HTMLElement>(null);
  const handleClickProfilePopup = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElProfilePopup(event.currentTarget);
  };
  const handleCloseProfilePopup = () => {
    setAnchorElProfilePopup(null);
  };
  const openProfilePopup = Boolean(anchorElProfilePopup);
  const idProfilePopup = openProfilePopup ? "profile-popup" : undefined;

  const [anchorElNotificationsPopup, setAnchorElNotificationsPopup] = useState<null | HTMLElement>(null);
  const handleCloseNotificationsPopup = () => {
    setAnchorElNotificationsPopup(null);
  };
  const openNotificationsPopup = Boolean(anchorElNotificationsPopup);
  const idNotificationsPopup = openNotificationsPopup ? "profile-popup" : undefined;

  // Logout
  const [logoutLoading, setLogoutLoading] = useState(false);
  const handleLogout = () => {
    setLogoutLoading(true);
    APILogout()
      .then(() => {
        removeCognitoTokensFromLocalStorage();
        setLogoutLoading(false);
        dispatch(authenticate(false, null));
        handleCloseProfilePopup();
        setTimeout(() => {
          navigate("/?login");
        }, 50);
      })
      .catch(() => {
        setLogoutLoading(false);
      });
  };

  // Mobile menu
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const renderMenuItem = (item: HeaderMenuItem, key: string | number) => {
    if (item.showWhenLoggedIn && !isLoggedIn) return null;
    if (item.showWhenLoggedOut && isLoggedIn) return null;
    if (item.hideMeFilter) return null;
    if (item.href) {
      // Internal navigation
      if (item.external) {
        return (
          <a
            href={item.href}
            target={item.newTab ? "_blank" : undefined}
            rel={item.newTab ? "noopener noreferrer" : undefined}
            onClick={() => setMobileMenuOpen(false)}
            className={item.highlight ? "rounded-lg bg-blue text-white px-4 py-2 custom-text-[poppins-500-14px]" : undefined}
          >
            {item.icon}
            {item.label}
          </a>
        );
      }
      return (
        <NavLink
          to={item.href}
          key={key}
          onClick={() => setMobileMenuOpen(false)}
          className={item.highlight ? "rounded-lg bg-blue text-white px-4 py-2 custom-text-[poppins-500-14px]" : undefined}
        >
          {item.icon}
          {item.label}
        </NavLink>
      );
    }
    if (item.action) {
      return (
        <button
          key={key}
          type="button"
          onClick={item.action}
          className={item.highlight ? "rounded-lg bg-blue text-white px-4 py-2 custom-text-[poppins-500-14px]" : "custom-text-[poppins-500-14px]"}
        >
          {item.icon}
          {item.label}
        </button>
      );
    }
    return null;
  };

  const renderNavigation = () => {
    return learnerMenu.navigation.map((item, idx) => {
      if (item.children && item.children.length > 0) {
        return (
          <div
            key={item.label}
            onClick={handleClickMorePopup}
            className="flex items-center gap-[7px] text-[#111827] hover:text-[#407EC9] cursor-pointer"
          >
            <button
              aria-describedby={idMorePopup}
              className="normal-case custom-text-[poppins-00-16px] leading-[22px]"
              type="button"
            >
              {item.label}
            </button>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7" fill="none">
              <path d="M10.2686 1.24199L5.48335 5.75586L0.698138 1.24199" stroke="#111827" strokeWidth="1.12847" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      }
      return renderMenuItem(item, idx);
    });
  };

  return (
    <>
      <div className="navbar-placeholder"></div>
      <section className={`navbar ${isMobileMenuOpen ? "mobile-open" : ""}`}>
        <div className="hamburger" onClick={toggleMobileMenu}>
          <div className={`bar ${isMobileMenuOpen ? "mobile-open" : ""}`}></div>
        </div>
        <div className="logo">
          {learnerMenu.logo.action ? (
            <button onClick={learnerMenu.logo.action} className="focus:outline-none">
              <img src={learnerMenu.logo.src} alt={learnerMenu.logo.alt} />
            </button>
          ) : (
            <NavLink to={learnerMenu.logo.href || "/"} onClick={() => setMobileMenuOpen(false)}>
              <img src={learnerMenu.logo.src} alt={learnerMenu.logo.alt} />
            </NavLink>
          )}
        </div>
        <div className={`menus ${isMobileMenuOpen ? "mobile-open" : ""}`}>
          <div className="navigation pt-[3px]">
            {renderNavigation()}
          </div>
        </div>
        <MorePopup
          id={idMorePopup}
          open={openMorePopup}
          anchorEl={anchorElMorePopup}
          handleClose={handleCloseMorePopup}
          items={
            learnerMenu.navigation.find((item) => item.children && item.label.toLowerCase() === "more")?.children ?? []
          }
        />
        <div className="actions md:ml-[114px]">
          {isLoggedIn && user ? (
            <div className="toolbar">
              <NotificationsPopup
                id={idNotificationsPopup}
                open={openNotificationsPopup}
                anchorEl={anchorElNotificationsPopup}
                handleClose={handleCloseNotificationsPopup}
              />
              <Link
                onClick={handleClickProfilePopup}
                component="button"
                underline="none"
                className="profile_icon"
              >
                <Avatar
                  className="!bg-[#d9d9d9] !font-poppins !text-[12px] font-semibold !text-black"
                  src={AVATARGetUserAvatarUrl(user)}
                  alt="profile"
                  sx={{
                    border: "2px solid rgba(64, 126, 201, 1)",
                  }}
                >
                  {initialsFromName((user.firstname ? user.firstname : "") + " " + (user.lastname ? user.lastname : ""))}
                </Avatar>
              </Link>
              <ProfilePopup
                id={idProfilePopup}
                open={openProfilePopup}
                anchorEl={anchorElProfilePopup}
                handleClose={handleCloseProfilePopup}
                handleLogout={handleLogout}
                logoutLoading={logoutLoading}
                user={user}
                actions={learnerMenu.actions || []}
              />
            </div>
          ) : (
            <div className="flex gap-[11px] pr-[41px]">
              <button onClick={learnerMenu.loginAction} className="rounded-lg bg-[#407EC9] w-[102px] h-[41px] !text-white transition-colors custom-text-[poppins-500-14px] cursor-pointer !no-underline flex items-center justify-center">
                {t("sign-in")}
              </button>
              <button onClick={learnerMenu.signUpAction} className="rounded-lg bg-[linear-gradient(180deg,#245A9E_0%,#5EA6FC_100%)] w-[102px] h-[41px] !text-white transition-all custom-text-[poppins-500-14px] cursor-pointer !no-underline flex items-center justify-center">
                {t("join-now")}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Header;
